/**
 * The dashboard's one query, GET /api/dashboard/summary. It carries every
 * figure the screen shows (the engine's, golden-parity tested), already
 * partitioned and ordered: `food_cost_health` in the OD-P8 order (over,
 * estimate, ok, no sell price), `health_counts`, the stock value and the
 * "Before service" lists. Nothing here recomputes a figure;
 * features/dashboard/dashboardModel.ts only picks words and order.
 *
 * Welcome reads the same query for its step counts (`counts`, including
 * `prep_lists`), and the API answers it before onboarding is done, so the
 * counts show from a chef's first visit.
 */
import { queryOptions, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api, unwrap } from '@/api/client';
import type { components } from '@/api/schema';
import { registerLive, type LiveEvent } from '@/lib/live';

export type DashboardSummary = components['schemas']['DashboardSummaryOut'];
export type DashDishRow = components['schemas']['DashDishRow'];
export type NeedsAttentionItem = components['schemas']['NeedsAttentionItem'];
export type UnpricedIngredientItem = components['schemas']['UnpricedIngredientItem'];
export type RecentActivityItem = components['schemas']['RecentActivityItem'];

export const dashboardKey = ['dashboard', 'summary'] as const;

export const dashboardSummaryQuery = queryOptions({
  queryKey: dashboardKey,
  queryFn: () => unwrap(api.GET('/api/dashboard/summary')),
});

export function useDashboardSummary() {
  return useQuery(dashboardSummaryQuery);
}

/**
 * Standalone quick-save for the kitchen target food cost (POST
 * /api/dashboard/target-food-cost). No screen uses it: the Settings screen's
 * "Target food cost" field (part of the one PATCH /api/settings save) is the
 * editing surface. Kept so a future dashboard control can use it without a
 * new query file.
 */
export function useSaveTargetFoodCost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (target_food_cost_pct: number) => unwrap(api.POST('/api/dashboard/target-food-cost', { body: { target_food_cost_pct } })),
    onSuccess: (data) => qc.setQueryData(dashboardKey, data),
  });
}

// costs.changed (lib/live.ts) already refreshes ['dashboard', ...] for any
// price, labour or target change. These cover changes that do not always
// move a cost: a new ingredient with no price yet, a menu rename, a soft
// delete or restore, and stock. Stock moves (a stocktake, a delivery) publish
// `ingredient.stock_updated`, which the 'ingredient.' handler catches; the
// 'stocktake.' and 'delivery.' handlers are belt and braces, so the Stock on
// hand figure follows even an event that carries no ingredient change.
const refreshDashboard = (_evt: LiveEvent, qc: QueryClient) => void qc.invalidateQueries({ queryKey: ['dashboard'] });
registerLive('ingredient.', refreshDashboard);
registerLive('recipe.', refreshDashboard);
registerLive('menu.', refreshDashboard);
registerLive('stocktake.', refreshDashboard);
registerLive('delivery.', refreshDashboard);
